import React from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import localeText from "../../../../../../../locale";
import FormSectionHeader from "../components/FormSectionHeader";
import ToggleField from "../components/ToggleField";
import RichTextField from "../components/RichTextField";
import { DOWNLOAD_FILE_TYPES, PRODUCT_STATES } from "../constants";

function ProductDetailsSection({ form }) {
  const isService = form.productType === 2;
  const isDigital = form.productType === 3;
  const isPhysical = form.productType === 1;
  const showStock = !isDigital && !form.hasVariations;
  const svgIcon = (
    <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.675 0H15.225C15.388 0 15.5488 0.0379522 15.6946 0.110851C15.8404 0.18375 15.9672 0.289594 16.065 0.42L18.9 4.2V19.95C18.9 20.2285 18.7894 20.4955 18.5925 20.6925C18.3956 20.8894 18.1285 21 17.85 21H1.05C0.771523 21 0.504451 20.8894 0.307538 20.6925C0.110625 20.4955 0 20.2285 0 19.95V4.2L2.835 0.42C2.9328 0.289594 3.05963 0.18375 3.20543 0.110851C3.35122 0.0379522 3.51199 0 3.675 0ZM16.275 4.2L14.7 2.1H4.2L2.625 4.2H16.275ZM6.3 8.4H4.2V10.5C4.2 11.8924 4.75312 13.2277 5.73769 14.2123C6.72226 15.1969 8.05761 15.75 9.45 15.75C10.8424 15.75 12.1777 15.1969 13.1623 14.2123C14.1469 13.2277 14.7 11.8924 14.7 10.5V8.4H12.6V10.5C12.6 11.3354 12.2681 12.1366 11.6774 12.7274C11.0866 13.3181 10.2854 13.65 9.45 13.65C8.61457 13.65 7.81335 13.3181 7.22261 12.7274C6.63187 12.1366 6.3 11.3354 6.3 10.5V8.4Z"
        fill="var(--theme-secondary)"
      />
    </svg>
  );

  return (
    <section className="product-form-card flex flex-column gap-4">
      <FormSectionHeader
        icon={svgIcon}
        title={localeText(isService ? "addServiceDetails" : "addProductDetails")}
        subtitle={localeText(isService ? "addServiceDetailsSubtitle" : "addProductDetailsSubtitle")}
      />

      {isDigital ? (
        <div className="grid">
          <div className="col-12 md:col-8">
            <label className="product-form-label">
              {localeText("productName")}
              <span className="product-form-required">*</span>
            </label>
            <InputText
              value={form.title}
              onChange={(e) => form.setTitle(e.target.value)}
              placeholder={localeText("productNamePlaceholder")}
              className="w-full border-round-lg"
            />
          </div>
          <div className="col-12 md:col-4">
            <label className="product-form-label">
              {localeText("downloadFileType")}
              <span className="product-form-required">*</span>
            </label>
            <Dropdown
              value={form.downloadFileType}
              options={DOWNLOAD_FILE_TYPES.map((item) => ({
                label: localeText(item.labelKey),
                value: item.value,
              }))}
              onChange={(e) => form.setDownloadFileType(e.value)}
              placeholder={localeText("selectFileType")}
              className="w-full border-round-lg p-no-outline"
            />
          </div>
        </div>
      ) : (
        <>
          <div className={"grid"}>
            <div className={`col-12 md:col-${isPhysical ? "8" : "12"}`}>
              <label className="product-form-label">
                {localeText("productName")}
                <span className="product-form-required">*</span>
              </label>
              <InputText
                value={form.title}
                onChange={(e) => form.setTitle(e.target.value)}
                placeholder={localeText(
                  isService ? "serviceNamePlaceholder" : "productNamePlaceholder",
                )}
                className="w-full border-round-lg"
              />
            </div>
            {isPhysical && (
              <div className="col-12 md:col-4">
                <label className="product-form-label">
                  {localeText("productState")}
                  <span className="product-form-required">*</span>
                </label>
                <Dropdown
                  value={form.productState}
                  options={PRODUCT_STATES.map((item) => ({
                    label: localeText(item.labelKey),
                    value: item.value,
                  }))}
                  onChange={(e) => form.setProductState(e.value)}
                  className="w-full border-round-lg p-no-outline"
                />
              </div>
            )}
          </div>

          {isPhysical && (
            <div className="product-form-toggle-row">
              <ToggleField
                title={localeText("withVariation")}
                help={localeText("withVariationHelp")}
                checked={form.hasVariations}
                onChange={form.setHasVariations}
              />
              <ToggleField
                title={localeText("forSale")}
                help={localeText("forSaleHelp")}
                checked={form.publishNow}
                onChange={form.setPublishNow}
                disabled={!form.canPublishProducts}
              />
            </div>
          )}
          {!isPhysical && (
            <div>
              <ToggleField
                title={localeText("forSale")}
                help={localeText("forSaleHelp")}
                checked={form.publishNow}
                onChange={form.setPublishNow}
                disabled={!form.canPublishProducts}
              />
            </div>
          )}
        </>
      )}

      {isDigital && (
        <div>
          <ToggleField
            title={localeText("forSale")}
            help={localeText("forSaleHelp")}
            checked={form.publishNow}
            onChange={form.setPublishNow}
            disabled={!form.canPublishProducts}
          />
        </div>
      )}

      <div>
        <label className="product-form-label">
          {localeText("fullDescription")}
          <span className="product-form-required">*</span>
        </label>
        <RichTextField
          value={form.description}
          onChange={form.setDescription}
          placeholder={localeText(
            isService ? "fullDescriptionPlaceholderService" : "fullDescriptionPlaceholder",
          )}
        />
        <p className="product-form-help">{localeText("fullDescriptionTip")}</p>
      </div>

      {!isDigital && (
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full m-auto"
        >
          {showStock && (
            <div className="flex-1">
              <label className="product-form-label">
                {localeText("stock")}
                <span className="product-form-required">*</span>
              </label>
              <InputNumber
                value={form.stock}
                onValueChange={(e) => form.setStock(e.value ?? 0)}
                placeholder={localeText("stockPlaceholder")}
                className="w-full border-round-lg"
                min={0}
                disabled={form.isStockLocked}
              />
              {form.isStockLocked && (
                <p className="product-form-help">{localeText("banggoodStockLocked")}</p>
              )}
            </div>
          )}
          <div className="flex-1">
            <label className="product-form-label">
              {localeText("category")}
              <span className="product-form-required">*</span>
            </label>
            <Dropdown
              value={form.selectedCategory}
              options={form.categories}
              optionLabel="_label"
              onChange={(e) => {
                form.setSelectedCategory(e.value);
                form.setSelectedSubcategory(null);
              }}
              placeholder={localeText("selectCategory")}
              className="w-full border-round-lg p-no-outline"
              filter
            />
          </div>
          <div className="flex-1">
            <label className="product-form-label">
              {localeText("subcategory")}
              <span className="product-form-required">*</span>
            </label>
            <Dropdown
              value={form.selectedSubcategory}
              options={form.subcategories}
              optionLabel="_label"
              onChange={(e) => form.setSelectedSubcategory(e.value)}
              placeholder={localeText("selectSubcategory")}
              className="w-full border-round-lg p-no-outline"
              filter
              disabled={!form.selectedCategory}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export default ProductDetailsSection;
